import React from "react";
import { Dialog, Autocomplete, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Icon from "./Icon";
import useSessionLocations from "./useSessionLocations";
import "./sessionFormDialog.css";

const minutes = (t) => {
  const [h, m] = String(t || "").split(":").map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : NaN;
};

/** "3h 30m" for a positive span between two HH:mm times, otherwise null. */
export function formatDuration(start, end) {
  const span = minutes(end) - minutes(start);
  if (!(span > 0)) return null;
  const h = Math.floor(span / 60);
  const m = span % 60;
  return `${h}h${m ? ` ${m}m` : ""}`;
}

/** Names the required fields still blank or invalid; empty means the form can be saved. */
export function missingFields(form) {
  const missing = [];
  if (!form.date) missing.push("date");
  if (!formatDuration(form.startTime, form.endTime)) missing.push("valid time range");
  if (form.sessionType === "PHYSICAL" && !form.location) missing.push("location");
  if (form.sessionType === "VIRTUAL" && !form.meetingLink.trim()) missing.push("meeting link");
  return missing;
}

function Toggle({ checked, onChange, label, hint }) {
  return (
    <label className="sf-tog">
      <input type="checkbox" className="sf-vh" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="sf-sw" aria-hidden="true" />
      <span>
        <b>{label}</b>
        {hint && <span className="sf-hint">{hint}</span>}
      </span>
    </label>
  );
}

function LocationPicker({ form, setForm, canEdit }) {
  const loc = useSessionLocations({ form, setForm });
  // A session being edited may point at a location that is no longer in the saved list; keep it selectable.
  const names = loc.locations.map((l) => l.name);
  const cards = [
    ...loc.locations.map((l) => ({ key: l.id, name: l.name, id: l.id })),
    ...(form.location && !names.includes(form.location) ? [{ key: "current", name: form.location, id: null }] : []),
  ];

  return (
    <>
      {cards.length > 0 && (
        <div className="sf-locs" role="radiogroup" aria-label="Saved locations">
          {cards.map((c) => {
            const on = form.location === c.name;
            return (
              <div key={c.key} className={`sf-loc${on ? " on" : ""}`}>
                <label>
                  <input type="radio" name="sf-location" className="sf-vh" checked={on} onChange={() => loc.select(c.name)} />
                  <span className="sf-rad" aria-hidden="true" />
                  <span className="nm">{c.name}</span>
                </label>
                {canEdit && c.id != null && (
                  <button type="button" className="sf-rm" aria-label={`Remove saved place ${c.name}`} disabled={loc.busy} onClick={() => loc.remove(c.id)}>
                    <Icon n="x" s={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="sf-addrow">
        <Autocomplete
          freeSolo
          fullWidth
          disabled={!canEdit}
          value={null}
          inputValue={loc.query}
          onInputChange={(_, value) => loc.onInput(value)}
          onChange={(_, value) => loc.pick(value)}
          options={loc.suggestions}
          getOptionLabel={(o) => (typeof o === "string" ? o : o?.display_name || "")}
          filterOptions={(x) => x}
          isOptionEqualToValue={(o, v) => o?.display_name === (typeof v === "string" ? v : v?.display_name)}
          loading={loc.suggesting}
          noOptionsText="Type to search OpenStreetMap for locations..."
          loadingText="Searching locations..."
          slotProps={{ paper: { sx: { "& .MuiAutocomplete-listbox": { maxHeight: 300 } } } }}
          renderOption={(props, o) => (
            <li {...props} key={o?.place_id || o?.display_name}>{o.display_name}</li>
          )}
          renderInput={(params) => (
            <span className="sf-inp" ref={params.InputProps.ref}>
              <Icon n="search" s={15} />
              <input
                {...params.inputProps}
                aria-label="Search or add location (OpenStreetMap)"
                placeholder="Search for an address or place"
                autoComplete="new-password"
              />
            </span>
          )}
        />
        <button type="button" className="sf-btn out" disabled={loc.busy || !loc.query.trim() || !canEdit} onClick={loc.add}>
          <Icon n="plus" s={14} w={2.4} />Add
        </button>
      </div>
      <span className="sf-hint">
        {loc.suggesting ? "Searching…" : "New locations are saved for future sessions. Search powered by OpenStreetMap."}
      </span>
      {loc.error && <span className="sf-err" role="alert">{loc.error}</span>}
    </>
  );
}

/**
 * Add / edit session dialog. `form` is the SessionsTab form state (name, date, startTime, endTime, sessionType, location,
 * trackLocation, meetingLink, enabled, topics); the parent owns saving.
 */
export default function SessionFormDialog({ open, onClose, onSave, saving, canEdit, editTarget, number, course, sessionCount, form, setForm }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value);
  const duration = formatDuration(form.startTime, form.endTime);
  const missing = missingFields(form);
  const physical = form.sessionType === "PHYSICAL";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth={false}
      aria-labelledby="sf-title"
      PaperProps={{ sx: { width: fullScreen ? undefined : "min(760px, 100%)", borderRadius: fullScreen ? 0 : "12px", maxHeight: fullScreen ? undefined : "calc(100vh - 48px)" } }}
    >
      <div className="sf">
        <div className="sf-head">
          <div>
            <h2 id="sf-title">{editTarget ? "Edit session" : "Add session"}</h2>
            <p>
              {course?.code && <><span className="code">{course.code}</span> · </>}
              {course?.title && <>{course.title} · </>}
              {sessionCount} session{sessionCount === 1 ? "" : "s"} scheduled
            </p>
          </div>
          <button type="button" className="sf-close" aria-label="Close" onClick={onClose}><Icon n="x" s={18} /></button>
        </div>

        <div className="sf-body">
          <div className="sf-sec">
            <h4>Session</h4>
            <div className="sf-fields">
              <div className="sf-row r12">
                <label className="sf-f">Number
                  <span className="sf-inp ro"><Icon n="hash" s={15} /><input className="mono" value={number} readOnly /></span>
                </label>
                <label className="sf-f">Name
                  <span className="sf-inp"><input placeholder="e.g. Hose-line drills & forcible entry" value={form.name || ""} onChange={text("name")} /></span>
                </label>
              </div>
              <label className="sf-f">Topics covered
                <textarea placeholder="One topic per line" value={form.topics || ""} onChange={text("topics")} />
                <span className="sf-hint">Shown when students or instructors expand this session.</span>
              </label>
            </div>
          </div>

          <div className="sf-sec">
            <h4>When</h4>
            <div className="sf-fields">
              <div className="sf-row r3">
                <label className="sf-f"><span>Date <span className="sf-req" aria-hidden="true">*</span></span>
                  <span className="sf-inp"><Icon n="cal" s={15} /><input type="date" required value={form.date || ""} onChange={text("date")} /></span>
                </label>
                <label className="sf-f">Start
                  <span className="sf-inp"><Icon n="clock" s={15} /><input type="time" value={form.startTime} onChange={text("startTime")} /></span>
                </label>
                <label className="sf-f">End
                  <span className="sf-inp"><Icon n="clock" s={15} /><input type="time" value={form.endTime} onChange={text("endTime")} /></span>
                </label>
              </div>
              <div className={`sf-dur${duration ? "" : " bad"}`} role="status">
                {duration ? <>Duration <b>{duration}</b></> : <b>End time must be after start time</b>}
              </div>
            </div>
          </div>

          <div className="sf-sec">
            <h4>Where</h4>
            <div className="sf-fields">
              <div className="sf-seg" role="radiogroup" aria-label="Session type">
                <label className={physical ? "on" : ""}>
                  <input type="radio" name="sf-type" className="sf-vh" checked={physical} onChange={() => set("sessionType")("PHYSICAL")} />
                  <Icon n="pin" s={16} />Physical location
                </label>
                <label className={physical ? "" : "on"}>
                  <input type="radio" name="sf-type" className="sf-vh" checked={!physical} onChange={() => set("sessionType")("VIRTUAL")} />
                  <Icon n="video" s={16} />Virtual meeting
                </label>
              </div>
              {physical ? (
                <LocationPicker form={form} setForm={setForm} canEdit={canEdit} />
              ) : (
                <label className="sf-f">Meeting link
                  <span className="sf-inp"><Icon n="link" s={15} />
                    <input placeholder="https://meet.google.com/... or https://zoom.us/..." value={form.meetingLink} onChange={text("meetingLink")} />
                  </span>
                </label>
              )}
            </div>
          </div>

          <div className="sf-sec">
            <h4>Check-in</h4>
            <div className="sf-fields">
              {physical && (
                <Toggle checked={form.trackLocation} onChange={set("trackLocation")} label="Track location (GPS/IP)" hint="Records where each student checks in from." />
              )}
              <Toggle
                checked={form.enabled === true}
                onChange={set("enabled")}
                label="Enabled"
                hint={form.enabled ? "Students can see and check in to this session." : "Hidden from students until enabled."}
              />
            </div>
          </div>
        </div>

        <div className="sf-foot">
          <span className="msg" aria-live="polite">{missing.length ? `Required: ${missing.join(", ")}` : "Ready to save"}</span>
          <button type="button" className="sf-btn ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="sf-btn pri" disabled={saving || missing.length > 0} onClick={onSave}>
            <Icon n="check" s={14} w={2.4} />{saving ? "Saving…" : "Save session"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
